/**
 * OmOssPage - About us page
 *
 * SEO Target: /om-oss
 * Primary keywords: om Kvanten, svensk årsredovisning
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, getOrganizationSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export function OmOssPage() {
  const breadcrumbItems = [{ label: "Om oss" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Om oss", url: "/om-oss" },
  ]);

  const organizationSchema = getOrganizationSchema();

  return (
    <>
      <PageMeta
        title="Om Kvanten - Sveriges enklaste verktyg för årsredovisning"
        description="Kvanten utvecklades för att göra årsredovisning enklare för svenska småföretag. Regeldriven automatisering, svensk support och utvecklat i Sverige."
        keywords="om Kvanten, svensk årsredovisning, K2 mjukvara"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/om-oss`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Om Kvanten</h1>
            <p className="text-xl text-gray-600 mb-8">
              Vi bygger verktyg som gör det enklare för svenska företagare att hantera sitt
              företag. Vår vision: En sak, gjord perfekt.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Vår mission</h2>
              <p className="text-gray-600 mb-6">
                Varje år lägger svenska småföretagare miljontals timmar och miljarder kronor på
                att uppfylla administrativa krav. Årsredovisningen är ett av de mest tidskrävande
                - och ofta dyraste - momenten.
              </p>
              <p className="text-gray-600 mb-6">
                Vi såg ett problem: Årsredovisning är i grunden en regelbaserad process. Om man
                har rätt data (SIE-filen) och följer regelverket (K2/K3) borde det mesta kunna
                automatiseras.
              </p>
              <p className="text-gray-600">
                Kvanten föddes ur den insikten. Istället för att tvinga användaren genom ett
                förutbestämt formulär, analyserar vi din SIE-fil och visar bara det som är
                relevant för just ditt företag.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Våra principer</h3>
              <div className="space-y-6">
                {[
                  {
                    title: "Regeldriven, inte stegdriven",
                    description:
                      "Wizarden styrs av K2-regler och SIE-data, inte av förutbestämda steg.",
                  },
                  {
                    title: "Automatisera allt som går",
                    description:
                      "Systemet beräknar, validerar och genererar. Användaren fyller endast i luckor.",
                  },
                  {
                    title: "Compliance by default",
                    description:
                      "Användaren kan inte skapa en ogiltig årsredovisning. Blockers stoppar processen.",
                  },
                  {
                    title: "Ingen betalning förrän värde levererats",
                    description:
                      "Hela wizarden genomförs gratis. Betalning sker först vid export.",
                  },
                ].map((principle, i) => (
                  <div key={i}>
                    <h4 className="font-semibold text-gray-900">{principle.title}</h4>
                    <p className="text-sm text-gray-600">{principle.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Vad vi gör</h2>
            <p className="text-xl text-gray-600">
              En komplett plattform för företagets årliga administration
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Årsredovisning",
                description:
                  "Digital K2- och K3-årsredovisning med automatisk SIE-import, validering och PDF/XML-export.",
                link: "/arsredovisning",
              },
              {
                title: "Digital aktiebok",
                description:
                  "Ägarförteckning enligt ABL 5 kap med historik, transaktioner och PDF-export.",
                link: "/aktiebok",
              },
              {
                title: "Protokoll",
                description:
                  "Automatiskt genererade årsstämmoprotokoll baserat på årsredovisningens data.",
                link: "/arsredovisning",
              },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.link}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Swedish */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Utvecklat i Sverige, för svenska företag
              </h2>
              <p className="text-gray-600 mb-6">
                Kvanten är byggt från grunden för att följa svenska regler och regelverk.
                Vi förstår BFNAR 2016:10 (K2), BFNAR 2012:1 (K3), årsredovisningslagen och
                aktiebolagslagen.
              </p>
              <ul className="space-y-3">
                {[
                  "Utvecklat av team i Sverige",
                  "Svensk support som förstår K2/K3",
                  "All data lagras i Sverige/EU",
                  "Följer GDPR och svensk dataskyddslag",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-center">
              <div className="bg-blue-50 rounded-2xl p-12 text-center">
                <svg className="w-24 h-16 mx-auto" viewBox="0 0 20 16" fill="none">
                  <rect width="20" height="16" fill="#006AA7" />
                  <rect x="6" width="2.5" height="16" fill="#FECC00" />
                  <rect y="6.5" width="20" height="3" fill="#FECC00" />
                </svg>
                <p className="mt-4 text-lg font-semibold text-gray-900">Made in Sweden</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Har du frågor?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Vi finns här för att hjälpa dig. Kontakta oss så återkommer vi inom 24 timmar.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Kontakta oss
            </Link>
            <Link
              to="/vanliga-fragor"
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Vanliga frågor
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
